import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCachePreheatQueries, useCachePreheatQuery} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import {useMemo, useState} from 'react'

import {IssueViewerViewGraphqlQuery} from '../components/IssueViewer'
import {IssueViewerSecondaryGraphqlQuery} from '../components/IssueViewerSecondaryView'
import {computeRecentActivitySince} from '../components/recent-agent-activity/use-recent-agent-activity'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  allowedOwner?: string
  tabbedTimeline?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
  tabbedTimeline?: boolean
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  allowedOwner,
  tabbedTimeline,
}: UseCachePreheatIssueViewerProps) {
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const primaryVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      count: 15,
      allowedOwner,
      tabbedTimeline: tabbedTimeline ?? false,
      ...(tabbedTimeline ? {viewType: 'CONVERSATION'} : {}),
    }),
    [owner, repo, number, allowedOwner, tabbedTimeline],
  )

  const [recentActivitySince] = useState(computeRecentActivitySince)
  const issueTriageAgentActivityEnabled = isFeatureEnabled('issues_recent_agent_activity')

  const secondaryVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      markAsRead: false,
      recentActivitySince: issueTriageAgentActivityEnabled ? recentActivitySince : undefined,
      issueTriageAgentActivityEnabled,
    }),
    [owner, repo, number, recentActivitySince, issueTriageAgentActivityEnabled],
  )

  useCachePreheatQuery({
    query: IssueViewerViewGraphqlQuery,
    variables: primaryVariables,
    source,
    excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
    highPriority: true,
  })

  useCachePreheatQuery({
    query: IssueViewerSecondaryGraphqlQuery,
    variables: secondaryVariables,
    source,
    excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
  })
}

export function useCachePreheatIssueViewerQueries({
  issues,
  source,
  tabbedTimeline,
}: UseCachePreheatIssueViewerQueriesProps) {
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const [recentActivitySince] = useState(computeRecentActivitySince)
  const issueTriageAgentActivityEnabled = isFeatureEnabled('issues_recent_agent_activity')

  const queries = useMemo(() => {
    const primaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerViewGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        count: 15,
        tabbedTimeline: tabbedTimeline ?? false,
        ...(tabbedTimeline ? {viewType: 'CONVERSATION'} : {}),
      },
      source,
      excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
      highPriority: true,
    }))

    const secondaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerSecondaryGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        markAsRead: false,
        recentActivitySince: issueTriageAgentActivityEnabled ? recentActivitySince : undefined,
        issueTriageAgentActivityEnabled,
      },
      source,
      excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
    }))

    return [...primaryQueries, ...secondaryQueries]
  }, [
    issues,
    source,
    tabbedTimeline,
    excludeFromPrimaryQueryCacheKey,
    excludeFromSecondaryQueryCacheKey,
    recentActivitySince,
    issueTriageAgentActivityEnabled,
  ])

  useCachePreheatQueries({queries})
}
